import { MailOutlined, MessageOutlined } from "@ant-design/icons"
import { Radio, Tooltip } from "antd"
import React, { useEffect, useId, useState } from "react"
import { useTranslation } from "react-i18next"

import { getAutomationOptions } from "../../SettingsAutomation/api/services.ts"
import { channelReadiness } from "../automations/channels.ts"
import { useHubRights } from "./hub-rights.ts"

export type MessageChannel = "email" | "sms"

/** `false` only when the company is known not to have it set up; `null` is unknown. */
export type ChannelsConfigured = Record<MessageChannel, boolean | null>

const UNKNOWN: ChannelsConfigured = { email: null, sms: null }

/**
 * Whether the company has each channel set up, from the same readiness the
 * automation builder's "Configure SMS to use this channel." reads
 * (`/automations/options/` → `channels[].status`). Someone who may not read
 * those options gets "unknown", which never blocks a channel. `enabled`
 * false defers the request, e.g. until a menu opens.
 */
export const useChannelsConfigured = (enabled = true): ChannelsConfigured => {
  const { automationsView } = useHubRights()
  const [configured, setConfigured] = useState<ChannelsConfigured>(UNKNOWN)
  useEffect(() => {
    if (!automationsView || !enabled) return
    let cancelled = false
    void getAutomationOptions()
      .then((response) => {
        if (cancelled || !response?.ok || !response.data?.channels) return
        const readiness = channelReadiness(response.data)
        setConfigured({
          email: readiness.EMAIL.available,
          sms: readiness.SMS.available
        })
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [automationsView, enabled])
  return configured
}

/** The configured channel to start on when `wanted` is not set up; `wanted` otherwise. */
export const configuredChannelFor = (
  wanted: MessageChannel,
  configured: ChannelsConfigured
): MessageChannel => {
  const other: MessageChannel = wanted === "email" ? "sms" : "email"
  return configured[wanted] === false && configured[other] !== false
    ? other
    : wanted
}

export const notConfiguredKey = (channel: MessageChannel) =>
  `commHub.channelNotConfigured.${channel}`

const CHANNELS: readonly MessageChannel[] = ["email", "sms"]

const ICON: Record<MessageChannel, React.ReactNode> = {
  email: <MailOutlined aria-hidden />,
  sms: <MessageOutlined aria-hidden />
}

interface ChannelToggleProps {
  value?: MessageChannel
  onChange?: (channel: MessageChannel) => void
  configured: ChannelsConfigured
  name: string
  "aria-label"?: string
  className?: string
  icons?: boolean
  /** Unavailable for another reason (a right), with nothing to explain. */
  disabled?: Partial<Record<MessageChannel, boolean>>
  /** Both held, e.g. while a file is uploading. */
  locked?: boolean
}

/**
 * The Email / SMS button pair. A channel the company has not set up is faded,
 * cannot be picked and says "needs to be configured" on hover and on keyboard
 * focus: it stays focusable (aria-disabled, never `disabled`), as in the
 * automation builder's channel picker.
 */
export const ChannelToggle = ({
  value,
  onChange,
  configured,
  name,
  className,
  icons = true,
  disabled,
  locked = false,
  "aria-label": ariaLabel
}: ChannelToggleProps) => {
  const { t } = useTranslation()
  const idPrefix = useId()
  const notConfigured = (channel: MessageChannel) =>
    configured[channel] === false
  return (
    <Radio.Group
      name={name}
      className={className}
      optionType="button"
      value={value}
      aria-label={ariaLabel}
      onChange={(event) => {
        const next = event.target.value as MessageChannel
        if (!locked && !notConfigured(next)) onChange?.(next)
      }}
    >
      {CHANNELS.map((channel) => {
        const blocked = notConfigured(channel)
        const button = (
          <Radio.Button
            key={channel}
            value={channel}
            disabled={!blocked && (locked || !!disabled?.[channel])}
            aria-disabled={blocked || undefined}
            className={blocked ? "cursor-not-allowed opacity-50" : undefined}
          >
            <span
              className={
                icons ? "inline-flex items-center gap-2 px-3" : undefined
              }
            >
              {icons && ICON[channel]}
              {t(`commHub.channel.${channel}`)}
            </span>
          </Radio.Button>
        )
        if (!blocked) return button
        // The tooltip names the option's aria-describedby after its own id,
        // and is rendered up front so that description exists before it opens.
        return (
          <Tooltip
            key={channel}
            id={`${idPrefix}-${channel}-not-configured`}
            title={t(notConfiguredKey(channel))}
            trigger={["hover", "focus"]}
            forceRender
          >
            {button}
          </Tooltip>
        )
      })}
    </Radio.Group>
  )
}

/**
 * A menu entry for a channel. Not set up, it is disabled with the reason
 * under its label, as the bulk bar's menus show a disabled entry's reason.
 */
export const channelMenuLabel = (
  label: string,
  reason: string | null
): React.ReactNode =>
  reason ? (
    <span className="flex flex-col">
      <span>{label}</span>
      <span className="text-xs text-[var(--dk-text-secondary,#6B7280)]">
        {reason}
      </span>
    </span>
  ) : (
    label
  )
